import { baseApi } from '@/Redux/baseApi';
import type { WebhookDeliveryStatus, WebhookEndpointStatus } from '@/lib/shared-types';

/**
 * # The whole §3 webhooks contract, pinned in one file
 *
 * Read directly from `src/modules/webhooks/` on `integrate/wave-a` (merged,
 * green — see `docs/ADR/ADR-0004-webhook-payload-contract.md`):
 *   - `webhooks.controller.ts` for every route below
 *   - `dto/*.ts` for the request bodies
 *   - `webhooks.service.ts` for the response shapes, in particular
 *     `ENDPOINT_PUBLIC_SELECT` and `DELIVERY_LIST_SELECT` — neither is
 *     decorated with `@ApiResponse`
 *   - `contract/payload-types.ts` and `contract/event-catalogue.ts` for the
 *     public payload contract. `WebhookEnvelope` below is copied from there
 *     verbatim rather than re-derived, because it is the one shape this
 *     screen must never guess at: a sample payload shown here is only honest
 *     if it is typed against the same interface the backend translates into,
 *     never against `src/events/event-payloads.ts`'s internal shapes.
 *
 * ## `secret` appears on exactly two response types below
 *
 * `WebhookEndpointWithSecret` — the answer to `create` and `rotateSecret`, and
 * nothing else ever carries it. There is no "reveal" endpoint and this file
 * defines no way to ask for one: the backend does not have a read path that
 * returns it, by design (ADR-0004, "the secret is returned exactly twice in
 * its life").
 *
 * ## Reads only
 *
 * Mutations (create, update, rotate-secret, test, disable, enable, delete,
 * replay) go through the `api` axios client directly from the component that
 * performs them — the create and rotate responses carry a live secret that
 * must be handed straight to a "shown once" dialog and never round-tripped
 * through the cache, and several refusals (409 `WEBHOOK_ENDPOINT_DISABLED`,
 * 503 `WEBHOOK_SIGNING_KEY_NOT_CONFIGURED`) carry a `code` a screen branches
 * on, which is easiest to read at the call site.
 */

export const WEBHOOKS_ROUTES = {
  eventTypes: '/webhooks/event-types',
  endpoints: '/webhooks/endpoints',
  endpoint: (id: string) => `/webhooks/endpoints/${id}`,
  endpointHealth: (id: string) => `/webhooks/endpoints/${id}/health`,
  endpointRotateSecret: (id: string) => `/webhooks/endpoints/${id}/rotate-secret`,
  endpointTest: (id: string) => `/webhooks/endpoints/${id}/test`,
  endpointDisable: (id: string) => `/webhooks/endpoints/${id}/disable`,
  endpointEnable: (id: string) => `/webhooks/endpoints/${id}/enable`,
  deliveries: '/webhooks/deliveries',
  delivery: (id: string) => `/webhooks/deliveries/${id}`,
  deliveryReplay: (id: string) => `/webhooks/deliveries/${id}/replay`,
} as const;

/**
 * The four keys `src/common/constants/permissions.ts` registers under
 * `webhooks`, split by blast radius rather than by CRUD verb. `manage` can
 * point a venue's order and payment stream at a URL of someone's choosing, so
 * it is deliberately not folded into `settings.update`. `rotate_secret` is
 * separate because it is the one route that hands back a live signing
 * secret. `replay` is separate for the opposite reason — the least dangerous
 * of the four, and the one an operations lead needs in a hurry.
 */
export const WEBHOOKS_PERMISSIONS = {
  view: 'webhooks.view',
  manage: 'webhooks.manage',
  replay: 'webhooks.replay',
  rotateSecret: 'webhooks.rotate_secret',
} as const;

export const WEBHOOK_ENDPOINT_DISABLED_CODE = 'WEBHOOK_ENDPOINT_DISABLED';
export const WEBHOOK_SIGNING_KEY_NOT_CONFIGURED_CODE = 'WEBHOOK_SIGNING_KEY_NOT_CONFIGURED';
export const WEBHOOK_URL_REFUSED_CODE = 'WEBHOOK_URL_REFUSED';
export const WEBHOOK_EVENT_TYPE_UNKNOWN_CODE = 'WEBHOOK_EVENT_TYPE_UNKNOWN';

/** The rotation window's ceiling and default, mirroring `RotateWebhookSecretDto` / `webhooks.service.ts`. */
export const WEBHOOK_ROTATION_DEFAULT_OVERLAP_MINUTES = 24 * 60;
export const WEBHOOK_ROTATION_MAX_OVERLAP_MINUTES = 30 * 24 * 60;

/** How many consecutive dead-lettered deliveries auto-disable an endpoint. Mirrors `delivery-policy.ts`. */
export const WEBHOOK_AUTO_DISABLE_AFTER = 5;

/** One initial attempt plus six backoff slots, spread over about nine hours. Mirrors `delivery-policy.ts`. */
export const WEBHOOK_MAX_ATTEMPTS = 7;

export interface WebhookPage<T> {
  items: T[];
  total: number;
  page: number;
  limit: number;
  pages: number;
}

const emptyPage = <T,>(page?: number, limit?: number): WebhookPage<T> => ({
  items: [],
  total: 0,
  page: page ?? 1,
  limit: limit ?? 25,
  pages: 1,
});

// -------------------------------------------------------------- event types

/** `GET /webhooks/event-types` row — `WEBHOOK_EVENT_CATALOGUE`, the public catalogue a tenant subscribes from. */
export interface WebhookEventTypeDefinition {
  type: string;
  version: number;
  description: string;
}

// ------------------------------------------------------------------ endpoint

/** `ENDPOINT_PUBLIC_SELECT` on the wire. Never `secret` or `previousSecret`. */
export interface WebhookEndpoint {
  id: string;
  tenantId: string;
  branchId: string | null;
  name: string;
  url: string;
  description: string | null;
  status: WebhookEndpointStatus;
  eventTypes: string[];
  secretVersion: number;
  /** The window's own expiry is public; the secret it protects is not. Null once the window has closed. */
  previousSecretExpiresAt: string | null;
  secretRotatedAt: string | null;
  lastSuccessAt: string | null;
  lastFailureAt: string | null;
  lastError: string | null;
  consecutiveFailures: number;
  disabledAt: string | null;
  /** The written sentence naming the host, the count and the last error — present only on DISABLED / DISABLED_FAILING. */
  disabledReason: string | null;
  createdAt: string;
  updatedAt: string;
}

/**
 * The one extra field `POST /endpoints` and `POST /endpoints/:id/rotate-secret`
 * answer with. Shown once, in a dialog that does not close until the operator
 * has copied it, and never fetched again by any route in this file.
 */
export interface WebhookEndpointWithSecret extends WebhookEndpoint {
  secret: string;
}

export interface ListEndpointsQuery {
  page?: number;
  limit?: number;
  status?: WebhookEndpointStatus;
  branchId?: string;
  eventType?: string;
  search?: string;
}

export interface CreateWebhookEndpointBody {
  name: string;
  url: string;
  description?: string;
  eventTypes: string[];
  branchId?: string;
}

export interface UpdateWebhookEndpointBody {
  name?: string;
  url?: string;
  description?: string;
  eventTypes?: string[];
  /** Explicitly nullable: omit to leave alone, a string to scope to a branch, `null` to widen back to the whole venue. */
  branchId?: string | null;
}

export interface RotateWebhookSecretBody {
  /** Minutes the superseded secret keeps verifying. 0 revokes it immediately — the right answer only when it has leaked. */
  overlapMinutes?: number;
}

// ------------------------------------------------------------------- health

/** `GET /webhooks/endpoints/:id/health` — `WebhooksService.health`. */
export interface WebhookEndpointHealth {
  endpointId: string;
  status: WebhookEndpointStatus;
  lastSuccessAt: string | null;
  lastFailureAt: string | null;
  lastError: string | null;
  consecutiveFailures: number;
  disabledReason: string | null;
  /** Every `WebhookDeliveryStatus` is a key, zero-filled — not only the statuses this endpoint happens to have. */
  deliveries: Record<WebhookDeliveryStatus, number>;
}

// ---------------------------------------------------------------- delivery

/**
 * The public payload contract, copied verbatim from
 * `src/modules/webhooks/contract/payload-types.ts`. This is what a delivery's
 * `payload` field actually holds — never `src/events/event-payloads.ts`'s
 * internal shapes, which is the whole point of ADR-0004. Left loose
 * (`Record<string, unknown>`) for `data` rather than the full closed union:
 * this screen only ever displays the envelope, never branches on its fields,
 * and re-deriving the eight `WebhookXData` interfaces here would be exactly
 * the kind of second copy ADR-0004 warns can drift from the one that matters.
 */
export interface WebhookEnvelope {
  /** Stable across every attempt AND across a replay — what a receiver dedupes on. */
  id: string;
  type: string;
  version: number;
  occurredAt: string;
  tenant: { id: string };
  branch: { id: string } | null;
  data: Record<string, unknown>;
}

/** `DELIVERY_LIST_SELECT` on the wire — `GET /webhooks/deliveries` row. No `payload` (large; omitted from lists). */
export interface WebhookDeliveryListRow {
  id: string;
  endpointId: string;
  /** The id a receiver dedupes on. Stable across every attempt and across a replay. */
  eventId: string;
  eventType: string;
  eventVersion: number;
  occurredAt: string;
  branchId: string | null;
  status: WebhookDeliveryStatus;
  attempts: number;
  nextAttemptAt: string | null;
  lastStatusCode: number | null;
  lastError: string | null;
  lastDurationMs: number | null;
  deliveredAt: string | null;
  failedAt: string | null;
  deadLetteredAt: string | null;
  replayOfId: string | null;
  createdAt: string;
}

/** `GET /webhooks/deliveries/:id` — the list row plus the exact body that was signed and sent. */
export interface WebhookDeliveryDetail extends WebhookDeliveryListRow {
  payload: WebhookEnvelope;
}

export interface ListDeliveriesQuery {
  page?: number;
  limit?: number;
  endpointId?: string;
  status?: WebhookDeliveryStatus;
  eventType?: string;
  /** Every delivery of one event, across endpoints and across replays — the id a receiver dedupes on. */
  eventId?: string;
}

// ------------------------------------------------------------------ endpoints

export const webhooksApi = baseApi.injectEndpoints({
  endpoints: (build) => ({
    /** `GET /webhooks/event-types` — `webhooks.view`. */
    webhookEventTypes: build.query<WebhookEventTypeDefinition[], void>({
      query: () => ({ url: WEBHOOKS_ROUTES.eventTypes }),
      transformResponse: (response: unknown) => (Array.isArray(response) ? response : []),
      providesTags: [{ type: 'Webhook' as const, id: 'EVENT_TYPES' }],
    }),

    /** `GET /webhooks/endpoints` — `webhooks.view`. */
    webhookEndpoints: build.query<WebhookPage<WebhookEndpoint>, ListEndpointsQuery | void>({
      query: (params) => ({ url: WEBHOOKS_ROUTES.endpoints, params: params ?? undefined }),
      transformResponse: (response: unknown): WebhookPage<WebhookEndpoint> => {
        const p = response as Partial<WebhookPage<WebhookEndpoint>> | null;
        return Array.isArray(p?.items)
          ? {
              items: p!.items,
              total: p?.total ?? p!.items.length,
              page: p?.page ?? 1,
              limit: p?.limit ?? 25,
              pages: p?.pages ?? 1,
            }
          : emptyPage(1, 25);
      },
      providesTags: (result) => [
        { type: 'Webhook' as const, id: 'ENDPOINTS' },
        ...(result?.items ?? []).map((e) => ({ type: 'Webhook' as const, id: e.id })),
      ],
    }),

    /** `GET /webhooks/endpoints/:id` — `webhooks.view`. */
    webhookEndpoint: build.query<WebhookEndpoint | null, string>({
      query: (id) => ({ url: WEBHOOKS_ROUTES.endpoint(id) }),
      transformResponse: (response: unknown) =>
        response && typeof response === 'object' ? (response as WebhookEndpoint) : null,
      providesTags: (_r, _e, id) => [{ type: 'Webhook' as const, id }],
    }),

    /** `GET /webhooks/endpoints/:id/health` — `webhooks.view`. */
    webhookEndpointHealth: build.query<WebhookEndpointHealth | null, string>({
      query: (id) => ({ url: WEBHOOKS_ROUTES.endpointHealth(id) }),
      transformResponse: (response: unknown) =>
        response && typeof response === 'object' ? (response as WebhookEndpointHealth) : null,
      providesTags: (_r, _e, id) => [{ type: 'Webhook' as const, id: `health:${id}` }],
    }),

    /** `GET /webhooks/deliveries` — `webhooks.view`. `?status=DEAD_LETTERED` is the dead-letter queue. */
    webhookDeliveries: build.query<WebhookPage<WebhookDeliveryListRow>, ListDeliveriesQuery | void>({
      query: (params) => ({ url: WEBHOOKS_ROUTES.deliveries, params: params ?? undefined }),
      transformResponse: (response: unknown): WebhookPage<WebhookDeliveryListRow> => {
        const p = response as Partial<WebhookPage<WebhookDeliveryListRow>> | null;
        return Array.isArray(p?.items)
          ? {
              items: p!.items,
              total: p?.total ?? p!.items.length,
              page: p?.page ?? 1,
              limit: p?.limit ?? 25,
              pages: p?.pages ?? 1,
            }
          : emptyPage(1, 25);
      },
      providesTags: [{ type: 'Webhook' as const, id: 'DELIVERIES' }],
    }),

    /** `GET /webhooks/deliveries/:id` — `webhooks.view`. */
    webhookDelivery: build.query<WebhookDeliveryDetail | null, string>({
      query: (id) => ({ url: WEBHOOKS_ROUTES.delivery(id) }),
      transformResponse: (response: unknown) =>
        response && typeof response === 'object' ? (response as WebhookDeliveryDetail) : null,
      providesTags: (_r, _e, id) => [{ type: 'Webhook' as const, id: `delivery:${id}` }],
    }),
  }),
  overrideExisting: false,
});

export const {
  useWebhookEventTypesQuery,
  useWebhookEndpointsQuery,
  useWebhookEndpointQuery,
  useWebhookEndpointHealthQuery,
  useWebhookDeliveriesQuery,
  useWebhookDeliveryQuery,
} = webhooksApi;

/** An endpoint changed — created, edited, rotated, disabled, enabled or deleted. */
export function invalidateWebhookEndpoint(id?: string) {
  return webhooksApi.util.invalidateTags([
    { type: 'Webhook' as const, id: 'ENDPOINTS' },
    ...(id
      ? [
          { type: 'Webhook' as const, id },
          { type: 'Webhook' as const, id: `health:${id}` },
        ]
      : []),
  ]);
}

/** A delivery was replayed, or a test send happened. */
export function invalidateWebhookDeliveries(endpointId?: string) {
  return webhooksApi.util.invalidateTags([
    { type: 'Webhook' as const, id: 'DELIVERIES' },
    ...(endpointId
      ? [
          { type: 'Webhook' as const, id: endpointId },
          { type: 'Webhook' as const, id: `health:${endpointId}` },
        ]
      : []),
  ]);
}
